import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import toast from 'react-hot-toast';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Divider
} from '@mui/material';
import { httpRequest } from '../../../services/httpRequest';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import { format } from 'date-fns';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import {
  PaymentApplicationDetails,
  PaymentSummary,
  PermitFeeTypeName
} from 'src/types/RoadPermit/roadPermit.type';
import { isPastDate, requiredFieldStyle, shouldShowEdit } from 'src/utils/CommonFunctions';
import { handleError } from 'src/redux-store/slices/errorSlice';
import PageTabTitle from 'src/components/PageTabTitle';
import { Container } from '@mui/system';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import { sendNotification } from 'src/utils/sendNotification';
import CustomButtonOutlined from 'src/components/CustomButton/CustomButtonOutlined';

const EditApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socket = useSelector((store: RootState) => store.socket.socket);

  const messageBodyRef = useRef<string>('');
  const { applicationID, permit } = useParams();
  const [openApprovePopup, setOpenApprovePopup] = useState<boolean>(false);
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const townshipDetails = useSelector(
    (store: RootState) => store.township.townshipDetails
  );
  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );
  const [switchTo, setSwitchTo] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingState, setLoadingState] = useState<{
    update: boolean;
    approve: boolean;
    draft: boolean;
    draftProceedToPayment: boolean;
  }>({
    update: false,
    approve: false,
    draft: false,
    draftProceedToPayment: false
  });
  const [additionalEscrowFeeValue, setAdditionalEscrowFeeValue] =
    useState<string>('0');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    applicantName: '',
    phoneNumber: '',
    address: '',
    contractor: '',
    contractorAddress: '',
    emergencyRoadOpening: '',
    roadOpeningLocation: '',
    roadOpeningDimensions: {
      width: 0,
      length: 0,
      depth: 0,
      total: 0
    },
    scheduledDate: null,
    purpose: '',
    sketch: null as File | null,
    agreement: false,
    specialConditions: '',
    additionalEscrowFee: null,
    applicationStatus: '',
    approvedDate: null,
    applicantUserId: '',
    applicationNumber: ''
  });
  const [fees, setFees] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState<number>(0);
  const [initialFormData, setInitialFormData] = useState(formData);
  const [isChanged, setIsChanged] = useState(false);
  const [isFeesApplicable, setIsFeesApplicable] = useState<boolean>(false);
  const isManager = currentPermitDetails.currentPermitUserRole === 'Manager';

  const getFeeDetails = async () => {
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${currentPermitDetails.id}`
    );

    if (response.status === 'success') {
      const creditCardFeeItem = response.data.find(
        (item) => item.FeeTypeName === PermitFeeTypeName.CreditCardFee
      );
      if (creditCardFeeItem) {
        setTaxRate(parseFloat(creditCardFeeItem.FeeValue));
      }
    } else if (response.type === 'NETWORK_ERROR') {
    }
  };

  const fetchApplicationData = async () => {
    try {
      const url = `/road_permit_application/get?ApplicationID=${applicationID}`;
      const response = await httpRequest('GET', url);
      if (response?.status === 'success') {
        const data = response.data;
        // Populate form data
        const populatedData = {
          title: data.ApplicantType || '',
          applicantName: data.ApplicantName || '',
          phoneNumber: data.ApplicantPhoneNo || '',
          address: data.ApplicantAddress || '',
          contractor: data.Contractor || '',
          contractorAddress: data.ContractorAddress || '',
          emergencyRoadOpening: data.Emergency ? 'yes' : 'no',
          roadOpeningLocation: data.RoadOpeningLocation || '',
          roadOpeningDimensions: {
            width: parseFloat(data.FtWide) || 0,
            length: parseFloat(data.FtLong) || 0,
            depth: parseFloat(data.FtDeep) || 0,
            total: parseFloat(data.TotalSqFootage) || 0
          },
          scheduledDate: format(new Date(data.WorkDate), 'MM-dd-yyyy'),
          purpose: data.Purpose || '',
          sketch: data.DocumentFileUpload || null,
          agreement: true,
          specialConditions: data.SpecialConditions || '',
          additionalEscrowFee: data.AdditionalEscrowFee || null,
          applicationStatus: data.ApplicationStatus,
          approvedDate: data.ApprovedDate,
          applicantUserId: data.UserID,
          applicationNumber: data.ApplicationNumber
        };
        setFormData(populatedData);
        setInitialFormData(populatedData);
        setAdditionalEscrowFeeValue(data.AdditionalEscrowFee || 0);
        const applicationFee = data.ApplicationFee
          ? Number(data.ApplicationFee)
          : 0;
        const inspectionFee = data.EscrowFee ? Number(data.EscrowFee) : 0;
        setFees({ applicationFee, inspectionFee });
        setIsFeesApplicable(applicationFee > 0 || inspectionFee > 0);
      } else if (response.type === 'NETWORK_ERROR') {
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      toast.error(error);
      dispatch(handleError(error));
    }
  };

  useEffect(() => {
    if (userDetails && applicationID) {
      fetchApplicationData();
      getFeeDetails();
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedForm = { ...formData }; // Start with a copy of the current formData
    if (type === 'checkbox') {
      updatedForm[name] = checked;
    } else if (name.startsWith('dimension')) {
      const dimensionName = name.split('.')[1];
      const dimensionValue = parseFloat(value) || 0;

      updatedForm.roadOpeningDimensions = {
        ...updatedForm.roadOpeningDimensions,
        [dimensionName]: dimensionValue // Store as a number
      };

      const total =
        updatedForm.roadOpeningDimensions.width *
        updatedForm.roadOpeningDimensions.length;
      updatedForm.roadOpeningDimensions.total = total;
    } else {
      updatedForm[name] = value;
    }

    // Set the updated form data in one call
    setFormData(updatedForm);
    setIsChanged(
      JSON.stringify(updatedForm) !== JSON.stringify(initialFormData)
    );
  };

  const basePath = `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}`;

  const getInvalidField = () => {
    const {
      title,
      applicantName,
      phoneNumber,
      address,
      emergencyRoadOpening,
      roadOpeningLocation,
      roadOpeningDimensions: { width, length, depth },
      scheduledDate,
      agreement
    } = formData;

    // Check each field and return the name of the first invalid field
    if (!title) return 'Applicant Title';
    if (!applicantName) return 'Applicant Name';
    if (!phoneNumber) return 'Phone Number';
    if (!address) return 'Applicant Address';
    if (!emergencyRoadOpening) return 'Emergency Road Opening';
    if (!roadOpeningLocation) return 'Road Opening Location';
    if (width <= 0 || length <= 0 || depth <= 0)
      return 'Road Opening Dimensions';
    if (!scheduledDate) return 'Scheduled Date';
    // if (sketch === null) return 'Document';
    if (!agreement) return 'Agreement';

    // If all fields are valid, return null
    return true;
  };

  const handleUpdateApplication = async (
    event: React.FormEvent,
    buttonType: string,
    status: string
  ) => {
    event.preventDefault();
    const isFormValid = getInvalidField();

    if (isFormValid !== true) {
      toast.error(`${isFormValid} is required`);
      return;
    }

    const payload = {
      ApplicantName: formData.applicantName,
      ApplicationStatus: formData.applicationStatus,
      ApplicantType: formData.title,
      ApplicantPhoneNo: formData.phoneNumber,
      ApplicantAddress: formData.address,
      Contractor: formData.contractor,
      ContractorAddress: formData.contractorAddress,
      FtWide: formData.roadOpeningDimensions.width,
      FtLong: formData.roadOpeningDimensions.length,
      FtDeep: formData.roadOpeningDimensions.depth,
      TotalSqFootage: formData.roadOpeningDimensions.total,
      WorkDate: formData.scheduledDate,
      Emergency: formData.emergencyRoadOpening === 'yes' ? 1 : 0,
      RoadOpeningLocation: formData.roadOpeningLocation,
      // TownshipID: townshipDetails.id,
      // UserID: userDetails.UserID,
      // DocumentFileUpload: formData.sketch,
      Purpose: formData.purpose,
      SpecialConditions: formData.specialConditions || null
    };

    setLoading(true);
    const response = await httpRequest(
      'PUT',
      `/road_permit_application/update/${applicationID}`,
      payload
    );
    if (response?.status === 'success') {
      if (buttonType === 'approve' ) return true
      toast.success('Application updated successfully.');

      if (buttonType === 'proceed-to-payment') {
        // Filter out the fees where value is > 0
        const paymentSummary: PaymentSummary = {
          fees: Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            Object.entries(fees).filter(([_, value]) => value > 0)
          ),
          ...(taxRate > 0 ? { taxRate } : {})
        };
        const paymentApplicationDetails: PaymentApplicationDetails = {
          applicationDetails: {
            userName: `${userDetails?.FirstName} ${userDetails?.LastName}`,
            applicationNumber: formData?.applicationNumber,
            applicationId: applicationID,
            applicationStatus: 'Draft',
            statusPayload: 'In Review',
            permitPrefix: 'Road'
          }
        };
        navigate(`/payment-methods`, {
          state: { paymentSummary, paymentApplicationDetails }
        });
      } else if (buttonType === 'update' && status === 'draft')
        navigate(`${basePath}/draft-application`);
      else navigate(`${basePath}/application-list`);
    } else if (response.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }

    setLoading(false);
  };

  function formatDate(isoDate) {
    const date = new Date(isoDate);

    const month = String(date.getMonth() + 1).padStart(2, '00');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }

  const handleChangeApplicationStatus = async (
    event: React.FormEvent,
    status: string
  ) => {
    event.preventDefault();

    setLoading(true);
    setLoadingState((prevState) => ({ ...prevState, approve: true }));
    const payload = {
      ApplicationID: applicationID,
      ApplicationStatus: status
    };

    const response = await httpRequest(
      'PUT',
      `/road_permit_application/update_status`,
      payload
    );
    if (response.status === 'success') {
      setLoading(false);
      if (status === 'Approved') {
        sendNotification(socket, {
          UserID: formData?.applicantUserId, 
          TownshipID: townshipDetails?.id,
          ApplicationID: applicationID,
          PermitID: permit,
          Title: 'Application Approved',
          Message: `Road Opening application with the no. ${formData?.applicationNumber} is approved successfully.`,
          ApplicationNumber: formData?.applicationNumber
        });
      }
      toast?.success(`Application Approved successfully.`);
      const targetPath =
        status === 'Approved'
          ? `${basePath}/application-list`
          : `${basePath}/draft-application`;
      navigate(targetPath);
    } else if (response.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }

    setLoadingState((prevState) => ({ ...prevState, approve: false }));
  };

  const handleMessageChange = (e) => {
    messageBodyRef.current = e.target.value;
  };

  const handleOnCancelClick = () => {
    if (formData.applicationStatus === 'Draft')
      navigate(`${basePath}/draft-application`);
    else navigate(`${basePath}/application-list`);
  };

  const handleNotifyUser = async (event) => {
    event.preventDefault();

    setLoading(true);
    const payload = {
      ApplicationID: applicationID,
      AdditionalEscrowFee: parseFloat(additionalEscrowFeeValue)
    };
    const response = await httpRequest(
      'POST',
      `/road_permit_application/notify_escrow_fee`,
      payload
    );
    if (response.status === 'success') {
      sendNotification(socket, {
        UserID: formData?.applicantUserId, // receiver user ID
        TownshipID: currentPermitDetails.townshipId,
        ApplicationID: applicationID,
        PermitID: permit,
        Title: 'Additional fee request',
        Message: `Please pay the additional fee of $${additionalEscrowFeeValue} for Application no. ${formData?.applicationNumber}.`,
        ApplicationNumber: formData?.applicationNumber
      });
      toast.success(response.message);
      setLoading(false);
      fetchApplicationData();
    } else {
      toast.error(response?.message);
      setLoading(false);
      dispatch(handleError(response?.message));
    }
  };

  const shouldSubmit = ()=>{
    const list = [
      'Home Owner',
      'Utility Company',
      'Public Service Electric & Gas(PSE&G)'
    ]

    return !list.includes(formData?.title)
  }

  const handleDirectSubmit = async () => {
    setLoading(true);
    const statusUpdatePayload = {
      ApplicationID: applicationID,
      ApplicationStatus: 'In Review',
      PermitID: permit,
      PermitPrefix: 'Road'
    };

    const statusResponse = await httpRequest(
      'POST',
      '/payments/update_status',
      statusUpdatePayload
    );

    if (statusResponse?.status === 'success') {
      setLoading(false);
      toast.success('Application Submitted successfully!');
      navigate(`${basePath}/application-list`)
    } else {
      setLoading(false);
      toast.error(statusResponse?.message);
    }
  };

  const handlePayAdditionalInspection = () => {
    const paymentSummary: PaymentSummary = {
      fees: {
        additionalInspectionFee: Number(additionalEscrowFeeValue)
      },
      ...(taxRate > 0 ? { taxRate } : {})
    };
    const paymentApplicationDetails: PaymentApplicationDetails = {
      applicationDetails: {
        userName: `${userDetails?.FirstName} ${userDetails?.LastName}`,
        applicationNumber: formData?.applicationNumber,
        applicationId: applicationID,
        applicationStatus: 'In Review'
      }
    };
    const additionalFeePayment = true;
    navigate(`/payment-methods`, {
      state: { paymentSummary, paymentApplicationDetails, additionalFeePayment }
    });
  };

  return (
    <Container
      className="main-table-container"
      sx={{ mt: 2, pb: { xs: '72px !important', sm: 0 } }}
    >
      <Breadcrumb PermitName={'Road Opening'} PageTitle={'Edit Application'} />
      {loading && <LoadingOverlay />}
      {/* PageTitle component */}
      <PageTabTitle title="Edit Application" />
      {/* Content block */}
      <Grid item md={3} sm={3} p={{ xs: '0px !important', sm: 'auto' }}>
        <FormHeaderGreeting
          name={`${userDetails?.FirstName} ${userDetails?.LastName}`}
          applicationNumber={formData?.applicationNumber}
          applicationStatus={formData?.applicationStatus}
          applicationType={'edit'}
        />
        <Card
          sx={{
            p: { xs: 1.5, md: 4 },
            pt: formData.applicationStatus == 'Approved' ? 2 : 4,
            minHeight: 'calc(100vh - 185px)'
          }}
        >
          {formData.applicationStatus == 'Approved' && !switchTo && (
            <Typography variant="body1" sx={{ mb: 2 }} color="green">
              This road opening application has been successfully issued on{' '}
              {formatDate(formData.approvedDate)}.
            </Typography>
          )}
          {switchTo !== '' ? (
            <EditFormfeatureViews
              switchTo={switchTo}
              setSwitchTo={setSwitchTo}
              applicationDetails={{
                applicationID: applicationID,
                TownshipID: townshipDetails.id,
                UserID: userDetails.UserID
              }}
            />
          ) : (
            <Box sx={{ mt: 1 }} className="newApp">
              <form>
                <Grid
                  container
                  spacing={{ sm: 3, xs: 2 }}
                  className="road_openning_edit_form"
                >
                  <Grid item xs={12} sm={6}>
                    <TextField
                      // select
                      label="Applicant Title"
                      name="title"
                      className="road_edit_form_adustment"
                      value={formData.title}
                      onChange={handleChange}
                      fullWidth
                      required
                      sx={requiredFieldStyle}
                      disabled={true}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    ></TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Applicant Name"
                      name="applicantName"
                      value={formData.applicantName}
                      onChange={handleChange}
                      className="road_edit_form_adustment"
                      fullWidth
                      required
                      sx={requiredFieldStyle}
                      disabled={!isManager}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Applicant Phone Number"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="road_edit_form_adustment"
                      fullWidth
                      required
                      sx={requiredFieldStyle}
                      disabled={!isManager}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Contractor"
                      name="contractor"
                      value={formData.contractor}
                      onChange={handleChange}
                      className="road_edit_form_adustment"
                      fullWidth
                      InputLabelProps={{
                        className: 'weight-600 road_edit_form_adustment'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className="txtArea road_edit_form_adustment"
                      label="Applicant Address"
                      placeholder="Applicant Address"
                      name="address"
                      value={formData.address}
                      multiline
                      fullWidth
                      required
                      sx={requiredFieldStyle}
                      rows={3}
                      onChange={handleChange}
                      disabled={!isManager}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} className="txtArea1">
                    <TextField
                      className="txtArea road_edit_form_adustment"
                      label="Contractor Address"
                      placeholder="Contractor Address"
                      name="contractorAddress"
                      value={formData.contractorAddress}
                      multiline
                      fullWidth
                      rows={3}
                      onChange={handleChange}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date Work is Scheduled to Begin"
                        value={new Date(formData.scheduledDate)}                     
                        sx={{ width: '100%' }}
                        onChange={(date) => {
                          const event = {
                            target: {
                              name: 'scheduledDate',
                              value: date,
                              type: 'date'
                            }
                          };
                          handleChange(event);
                          setFormData({
                            ...formData,
                            scheduledDate: format(new Date(date), 'MM-dd-yyyy')
                          });
                        }}
                        disablePast
                      />
                    </LocalizationProvider>
                    <Typography
                      sx={{ color: '#8B0000', fontSize: 14, margin: 1 }}
                    >
                      {isPastDate(formData?.scheduledDate)
                        ? "Work date can't be previous than current date."
                        : ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Road Opening Location"
                      name="roadOpeningLocation"
                      value={formData.roadOpeningLocation}
                      onChange={handleChange}
                      className="road_edit_form_adustment"
                      fullWidth
                      required
                      sx={requiredFieldStyle}
                      disabled={!isManager}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>

                  <Grid xs={12} sm={12} spacing={4}>
                    <Grid xs={12} sm={6} sx={{ mt: 2, ml: 3 }}>
                      <Typography variant="body1">
                        Road Opening Dimensions :
                      </Typography>
                    </Grid>
                    <Box display="flex" sx={{ mt: 2 }}>
                      <Grid xs={12}>
                        <Box display={{ sm: 'flex', xs: 'block' }}>
                          <Grid
                            item
                            xs={12}
                            sm={3}
                            sx={{ ml: { sm: 3, xs: 2 } }}
                          >
                            <TextField
                              label="Width (ft)"
                              name="dimension.width"
                              value={formData.roadOpeningDimensions.width}
                              onChange={handleChange}
                              className="road_edit_form_adustment"
                              fullWidth
                              required
                              sx={requiredFieldStyle}
                              disabled={!isManager}
                              InputLabelProps={{
                                className: 'weight-600'
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3} sx={{ ml: 2 }}>
                            <TextField
                              label="Length (ft)"
                              name="dimension.length"
                              value={formData.roadOpeningDimensions.length}
                              onChange={handleChange}
                              className="road_edit_form_adustment"
                              fullWidth
                              required
                              sx={requiredFieldStyle}
                              disabled={!isManager}
                              InputLabelProps={{
                                className: 'weight-600'
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3} sx={{ ml: 2 }}>
                            <TextField
                              label="Depth (ft)"
                              name="dimension.depth"
                              value={formData.roadOpeningDimensions.depth}
                              onChange={handleChange}
                              className="road_edit_form_adustment"
                              fullWidth
                              required
                              sx={requiredFieldStyle}
                              disabled={!isManager}
                              InputLabelProps={{
                                className: 'weight-600'
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3} sx={{ ml: 2 }}>
                            <TextField
                              label="Total Square Foot"
                              name="dimension.depth"
                              value={formData.roadOpeningDimensions.total}
                              onChange={handleChange}
                              className="road_edit_form_adustment"
                              fullWidth
                              required
                              sx={requiredFieldStyle}
                              disabled
                              InputLabelProps={{
                                className: 'weight-600'
                              }}
                            />
                          </Grid>
                        </Box>
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className="txtArea road_edit_form_adustment"
                      label="Purpose"
                      placeholder="Purpose"
                      name="purpose"
                      value={formData.purpose}
                      multiline
                      fullWidth
                      rows={3}
                      onChange={handleChange}
                      disabled={!isManager}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      className="txtArea road_edit_form_adustment"
                      label="Special Conditions"
                      placeholder="Special Conditions"
                      name="specialConditions"
                      value={formData.specialConditions}
                      multiline
                      fullWidth
                      rows={3}
                      onChange={handleChange}
                      disabled={!isManager}
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display={{ sm: 'flex' }} alignItems="center">
                      <Grid item mr={4}>
                        <Typography variant="body1">
                          Emergency Road Opening?
                        </Typography>
                      </Grid>
                      <Grid item>
                        <RadioGroup
                          name="emergencyRoadOpening"
                          value={formData.emergencyRoadOpening}
                          onChange={handleChange}
                          className="road_edit_form_adustment"
                          row
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                            disabled={!isManager}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                            disabled={!isManager}
                          />
                        </RadioGroup>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Grid item>
                      <CustomButtonOutlined
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                        title={'Upload/View Document'}
                        startIcon={<CloudUploadIcon />}
                        onClick={() => {
                          setSwitchTo('Upload/View Document');
                        }}
                      />
                      {
                        <>
                          {' '}
                          {formData.applicationStatus === 'In Review' &&
                            isManager && !shouldShowEdit(formData) && (
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{
                                  ml: { xs: 0, sm: 2 },
                                  mb: { xs: 2, sm: 0 },
                                  mt: { xs: 2, sm: 0 },
                                  width: { xs: '100%', sm: 'auto' }
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsOpen(true);
                                }}
                              >
                                Send Message
                              </Button>
                            )}
                          {formData.applicationStatus === 'In Review' &&
                            isManager && (
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{
                                  ml: { xs: 0, sm: 2 },
                                  mb: { xs: 2, sm: 0 },
                                  width: { xs: '100%', sm: 'auto' }
                                }}
                                onClick={() =>
                                  setSwitchTo('View Message History')
                                }
                              >
                                View Message History
                              </Button>
                            )}
                          {isFeesApplicable &&
                            formData.applicationStatus !== 'Draft' && (
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{
                                  ml: { sm: 2 },
                                  mb: { sm: 2, md: 0 },
                                  width: { xs: '100%', sm: 'auto' }
                                }}
                                onClick={() =>
                                  setSwitchTo('View Payment History')
                                }
                              >
                                View Payment History
                              </Button>
                            )}
                        </>
                      }
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: { xs: 'block', sm: 'flex' },
                      justifyContent: 'end'
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleOnCancelClick}
                      style={{ color: '#059EE3' }}
                      sx={{
                        border: '1px solid #059EE3',
                        '&:hover': {
                          borderColor: '#059EE3'              
                        },
                        ml: { xs: 0, sm: 2 },
                        mt: { xs: 2, sm: 0 },
                        width: { xs: '100%', sm: 'auto' },
                        mb: { xs: 'auto', sm: 2 }
                      }}
                    >
                      Cancel
                    </Button>

                    {formData.applicationStatus === 'Draft' && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ background: !isChanged ? '#E8EBED' : null }}
                          sx={{
                            ml: { xs: 0, sm: 2 },
                            mt: { xs: 2, sm: 0 },
                            mb: { xs: 'auto', sm: 2 },
                            width: { xs: '100%', sm: 'auto' },
                            px: { md: '20px', xs: 1 },
                            py: { md: '8px', xs: 0.8 },
                            fontSize: { md: '0.875rem', xs: '13px' }
                          }}
                          disabled={
                            !isChanged || isPastDate(formData?.scheduledDate)
                          }
                          onClick={(event) =>
                            handleUpdateApplication(event, 'update', 'draft')
                          }
                        >
                          Update
                        </Button>
                        {shouldSubmit() && <Button
                          variant="contained"
                          color="primary"
                          sx={{
                            ml: { xs: 0, sm: 2 },
                            mt: { xs: 2, sm: 0 },
                            mb: { xs: 'auto', sm: 2 },
                            width: { xs: '100%', sm: 'auto' },
                            px: { md: '20px', xs: 1 },
                            py: { md: '8px', xs: 0.8 },
                            fontSize: { md: '0.875rem', xs: '13px' }
                          }}
                          disabled={
                            isPastDate(formData?.scheduledDate)
                          }
                          onClick={handleDirectSubmit}
                        >
                          Submit
                        </Button>}
                      </>
                    )}

                    {formData.applicationStatus === 'Draft' &&
                      isFeesApplicable && (
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ background: null }}
                          sx={{
                            ml: { xs: 0, sm: 2 },
                            mb: { xs: 'auto', sm: 2 },
                            mt: { xs: 2, sm: 0 },
                            width: { xs: '100%', sm: 'auto' }
                          }}
                          disabled={isPastDate(formData?.scheduledDate)}
                          onClick={(event) =>
                            handleUpdateApplication(
                              event,
                              'proceed-to-payment',
                              'draft'
                            )
                          }
                        >
                          Proceed to Payment
                        </Button>
                      )}
                    {formData.applicationStatus !== 'Draft' && (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{
                          background:
                            !isChanged ||
                            formData.applicationStatus === 'Withdrawn' ||
                            formData.applicationStatus === 'Approved'
                              ? '#E8EBED'
                              : null
                        }}
                        sx={{
                          ml: { sm: 2 },
                          mb: { sm: 2 },
                          mt: { xs: 2, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        disabled={
                          isPastDate(formData?.scheduledDate) ||
                          !isChanged ||
                          formData.applicationStatus === 'Withdrawn' ||
                          formData.applicationStatus === 'Approved'
                        }
                        onClick={(event) =>
                          handleUpdateApplication(event, 'update', 'no-draft')
                        }
                      >
                        Update
                      </Button>
                    )}
                  </Grid>
                </Grid>

                {formData.applicationStatus !== 'Approved' &&
                  formData.applicationStatus !== 'Draft' && (
                    <>
                      <Divider sx={{ mt: '20px' }} />
                      <Grid
                        sx={{ mt: '20px' }}
                        sm={12}
                        display={{ xs: 'block', sm: 'flex' }}
                        justifyContent={'space-between'}
                      >
                        <Grid
                          xs={12}
                          sm={6}
                          sx={{
                            display: { xs: 'block', sm: 'flex' },
                            alignItems: 'center'
                          }}
                        >
                          {isFeesApplicable && (
                            <>
                              <FormControl
                                sx={{
                                  mx: { xs: 0, sm: 1 },
                                  my: { xs: 1.5, sm: 1 }
                                }}
                              >
                                <InputLabel
                                  className="weight-600"
                                  htmlFor="outlined-adornment-amount"
                                >
                                  Inspection Amount
                                </InputLabel>
                                <OutlinedInput
                                  id="outlined-adornment-amount"
                                  startAdornment={
                                    <InputAdornment position="start">
                                      $
                                    </InputAdornment>
                                  }
                                  label="Inspection-Amount"
                                  disabled={
                                    !isManager ||
                                    (isManager &&
                                      formData.additionalEscrowFee > 0)
                                  }
                                  value={
                                    Number(additionalEscrowFeeValue) === 0
                                      ? ''
                                      : additionalEscrowFeeValue
                                  }
                                  onChange={(e) => {
                                    const inputValue = e.target.value;

                                    // Regex to allow numbers with up to 2 decimal places
                                    if (
                                      /^\d{0,4}(\.\d{0,2})?$/.test(inputValue)
                                    ) {
                                      setAdditionalEscrowFeeValue(inputValue); // Update state as string
                                    }
                                  }}
                                  inputProps={{
                                    inputMode: 'decimal',
                                    pattern: '^[0-9]*[.]?[0-9]{0,2}$' // Pattern to allow numbers with up to 2 decimals
                                  }}
                                />
                              </FormControl>
                              {isManager && (
                                <Button
                                  type="submit"
                                  variant="contained"
                                  color="primary"
                                  style={{
                                    background: !Number(
                                      additionalEscrowFeeValue
                                    )
                                      ? '#E8EBED'
                                      : null
                                  }}
                                  // sx={{ ml: 2 }}
                                  sx={{
                                    ml: { sm: 2 },
                                    mb: { xs: 2, sm: 0 },
                                    width: { xs: '100%', sm: 'auto' }
                                  }}
                                  disabled={
                                    !Number(additionalEscrowFeeValue) ||
                                    formData.additionalEscrowFee > 0
                                  }
                                  onClick={handleNotifyUser}
                                >
                                  Notify User
                                </Button>
                              )}
                              {
                                <Button
                                  type="submit"
                                  variant="contained"
                                  color="primary"
                                  style={{
                                    background: !Number(
                                      additionalEscrowFeeValue
                                    )
                                      ? '#E8EBED'
                                      : null
                                  }}
                                  // sx={{ ml: 2 }}
                                  sx={{
                                    ml: { sm: 2 },
                                    mb: { xs: 2, sm: 0 },
                                    width: { xs: '100%', sm: 'auto' }
                                  }}
                                  disabled={
                                    !Number(additionalEscrowFeeValue) ||
                                    formData.additionalEscrowFee <= 0
                                  }
                                  onClick={handlePayAdditionalInspection}
                                >
                                  Pay Inspection
                                </Button>
                              }                            
                            </>
                          )}
                        </Grid>
                        {isManager &&
                          !(
                            formData.applicationStatus === 'Draft' ||
                            formData.applicationStatus === 'Withdrawn'
                          ) && (
                            <Grid
                              xs={12}
                              sm={6}
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{
                                  ml: { sm: 2 },
                                  mb: { xs: 2, sm: 0 },
                                  width: { xs: '100%', sm: 'auto' }
                                }}
                                disabled={isPastDate(formData?.scheduledDate)}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setOpenApprovePopup(true);
                                }}
                              >
                                Approve
                              </Button>
                            </Grid>
                          )}
                      </Grid>
                    </>
                  )}
              </form>
            </Box>
          )}
        </Card>
      </Grid>

      <CustomModal
        title={'Send Message'}
        IsOpen={IsOpen}
        setIsOpen={setIsOpen}
        messageBodyRef={messageBodyRef}
        actionButtons={['Cancel', 'Send']}
        loading={loading}
        setLoading={setLoading}
        applicationNumber={formData?.applicationNumber}
        ApplicationID={applicationID}
        applicationDetails={{ UserID: formData?.applicantUserId }}
        content={
          <TextField
            className="txtArea"
            label="Message"
            placeholder="Message"
            name="message"
            multiline
            onChange={(e) => handleMessageChange(e)}
            sx={{ width: '600px' }}
            rows={3}
          />
        }
      />

      <CustomModal
        title={'Approve Application?'}
        IsOpen={openApprovePopup}
        setIsOpen={setOpenApprovePopup}
        onSave={(event) => {
          event?.preventDefault();
          setOpenApprovePopup(false);
          const updated = handleUpdateApplication(
            event,
            'approve',
            'In Review'
          )
          if(updated) handleChangeApplicationStatus(event, 'Approved');
        }}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>
            Are you sure you want to approve this application?
          </Typography>
        }
      />
    </Container>
  );
};

export default EditApplication;
